import { z } from "zod";
import { manualPaymentMethodSchema, membershipCancelBySchema } from "../enums.ts";
import { filsSchema, isoDateSchema, isoDateTimeSchema, uuidSchema } from "./common.ts";

/**
 * A running monthly membership (M2, migration 0079), as the member home, the client page and the
 * Payments screen read it. An ended membership is not sent. Rules: `membership.ts`.
 */
export const membershipSummarySchema = z.object({
  id: uuidSchema,
  /** Who it was bought for (a label). */
  participantId: uuidSchema.nullable(),
  productId: uuidSchema,
  name: z.string(),
  unlimited: z.boolean(),
  /** The classes of the current month, with sessions the business added. Null: unlimited. */
  cap: z.number().int().nullable(),
  /** Classes used in the current month: booked, and late cancels and no-shows that lose the class. */
  used: z.number().int().min(0),
  /** The price of a month now. A renewal asks for it. */
  priceFils: filsSchema,
  /** The days of a month: a month starts on the day it is bought or renewed and runs these days. */
  periodDays: z.number().int().positive(),
  /** The paid month in force now. Null when the month of today is not paid. */
  periodStartsAt: isoDateTimeSchema.nullable(),
  periodEndsAt: isoDateTimeSchema.nullable(),
  /** The day the next month starts. In an unpaid month, the first day of that month. */
  nextStartsOn: isoDateSchema,
  /**
   * `active`: renews. `cancelling`: cancelled, it ends with the paid month (`nextStartsOn` is the
   * first day without it). `unpaid`: the month of today is not paid, so no class of it can be booked.
   */
  state: z.enum(["active", "cancelling", "unpaid"]),
  /** The open renewal request: pay it in the app, by the link, or at the desk. */
  renewal: z
    .object({
      requestId: uuidSchema,
      amountFils: filsSchema,
      dueOn: isoDateSchema,
      status: z.enum(["pending", "overdue"]),
      /** The payment link of the request (`/pay/r...`). Only for the business. */
      payLink: z.string().nullable().optional(),
    })
    .nullable(),
  cancelledBy: membershipCancelBySchema.nullable(),
});
export type MembershipSummary = z.infer<typeof membershipSummarySchema>;

/** `POST /business/businesses/:id/participants/:participantId/membership/cancel`: the owner ends a membership. */
export const ownerCancelMembershipRequestSchema = z.object({
  /** `period_end`: it ends with the paid month. `now`: it ends at once, bookings made stay. */
  at: z.enum(["period_end", "now"]),
  /**
   * The membership the screen shows. If it names a membership that has ended, the API makes no
   * change and returns the participant profile (a retry after a lost answer, review F13.1). Without
   * it, the API refuses with MEMBERSHIP_NOT_FOUND when no membership is running.
   */
  membershipId: uuidSchema.optional(),
});
export type OwnerCancelMembershipRequest = z.infer<typeof ownerCancelMembershipRequestSchema>;

/** `POST /business/businesses/:id/participants/:participantId/membership/renewal/record`: a renewal paid in cash or by transfer. */
export const recordMembershipRenewalRequestSchema = z.object({
  method: manualPaymentMethodSchema,
  note: z.string().trim().max(200).optional(),
  paidOn: isoDateSchema.optional(),
  /**
   * The request the screen shows. If the open request of the membership is a different one (that
   * month was paid elsewhere and the next one opened), the API refuses with
   * PAYMENT_REQUEST_NOT_PAYABLE and pays nothing (review F8.1).
   */
  requestId: uuidSchema.optional(),
  /**
   * A proof of payment uploaded before (`POST payment-proofs`): it goes on the payment this call
   * records. Only with a method: a sale to pay later records no payment.
   */
  proofId: uuidSchema.optional(),
});
export type RecordMembershipRenewalRequest = z.infer<typeof recordMembershipRenewalRequestSchema>;
